import { Card, Flex, Modal, Collapse } from 'antd';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CountUp from 'react-countup';
import moment from 'moment';
import { styled } from 'styled-components';
import { useNavigate } from 'react-router-dom';

import usecustomStyles from '../../Common/customStyles';
import '../../assets/scss/_custom.scss';
import * as Thunk from '../../slices/statistics/thunk';
import * as HPermission from '../../helpers/permission_helper';
import { ReactComponent as SvgEmergency } from '../../assets/images/svgs/light-emergency-on.svg';
import { ReactComponent as SvgTruck } from '../../assets/images/svgs/truck-tow.svg';
import { ReactComponent as SvgIndustry } from '../../assets/images/svgs/industry-windows.svg';
import { ReactComponent as SvgOilWell } from '../../assets/images/svgs/oil-well.svg';
import { ReactComponent as SvgDevice } from '../../assets/images/svgs/add-device.svg';
import { ReactComponent as SvgProject } from '../../assets/images/svgs/add-project.svg';
import { ReactComponent as SvgUsers } from '../../assets/images/svgs/users.svg';

const customStyles = usecustomStyles();

const createBgComponent = (color) => styled.div`
    background-color: ${color};
`;

const BgInfo = createBgComponent(({ theme }) => theme.token.colorInfoBg);
const BgPrimary = createBgComponent(({ theme }) => theme.token.colorPrimaryBg);
const BgSecondary = createBgComponent(({ theme }) => theme.token.colorSecondaryBg);
const BgSuccess = createBgComponent(({ theme }) => theme.token.colorSuccessBg);
// const BgDanger = createBgComponent(({ theme }) => theme.token.colorDangerBg);
const BgDevice = createBgComponent('#ecf2da');
const BgProject = createBgComponent('#dbeff35e');

const Widget = (props) => {
  const statistics = useSelector(state => state.Statistics);
  const user = useSelector(state => state.Login.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const role = user?.user?.role;

  /**
   * 显示月新增
   */
  const [showNewCard, setShowNewCard] = useState(false);

  useEffect(function () {
    const payload = {
      "startDate": '2023-01-01',
      "endDate": moment().format('YYYY-MM-DD'),
    }
    setShowNewCard(HPermission.hasPermission(role, HPermission.NEW_VIEW));

    // 只有当数据为空时才调用 API，避免页面刷新重复查询
    // if (!Object.keys(statistics.alarm).length) {
    //   dispatch(Thunk.alarm(payload)); 
    // }
    if (!Object.keys(statistics.project).length) {
      dispatch(Thunk.project());
    }
    if (!Object.keys(statistics.user).length) {
      dispatch(Thunk.user());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // 提取统计数据
  const project = statistics.project || {};
  const userStats = statistics.user || {};

  const projectCount = project.project || 0;
  const siteCount = project.site || 0;
  const vehicleCount = project.vehicle || 0;
  const onlineCount = project.onlineVehicle || 0;
  const offlineCount = vehicleCount - onlineCount;
  const newProject = project.newProject || 0;
  const newVehicle = project.newVehicle || 0;
  const userCount = Object.values(userStats).reduce((acc, value) => acc + value, 0);
  const onlineTodayCount = project.onlineVehicleCount;
  const alarmStyle = onlineCount < vehicleCount / 2;

  const handleNavigation = (path, restrictedRoles = []) => {
    if (!restrictedRoles.includes(role)) {
      navigate(path);
    }
  };

  const popProject = () => handleNavigation('/projects', ['ProjectAdmin', 'ProjectSupervisor']);
  const popSite = () => handleNavigation('/sites', ['ProjectSupervisor']);
  const popUsers = () => handleNavigation('/users', ['ProjectSupervisor']);
  const popVehicles = () => handleNavigation('/vehicles');


  const listModal = (dataList, title, isGrouped = false) => {
    let content;
    if (isGrouped) {
      const groupedData = dataList.reduce((acc, item) => {
        acc.set(item.projectName, [...(acc.get(item.projectName) || []), item]);
        return acc;
      }, new Map());

      const sortedGroupedData = Array.from(groupedData, ([projectName, vehicles]) => ({
        projectName,
        vehicles
      })).sort((a, b) => b.vehicles.length - a.vehicles.length);

      const panels = sortedGroupedData.map(({ projectName, vehicles }) => {
        const rows = [];
        for (let i = 0; i < vehicles.length; i += 4) {
          rows.push(vehicles.slice(i, i + 4));
        }
        return {
          projectName,
          rows
        };
      });

      content = (
        <div className="card-list-container">
          <Collapse>
            {panels.map(panel => (
              <Collapse.Panel key={panel.projectName} header={panel.projectName}>
                {panel.rows.map((row, index) => (
                  <div key={index}>
                    <li className='card-list-li' key={index}>
                      {row.map((vehicle, index) => (
                        <span key={index} style={{ margin: '0 8px 0 0' }}>{vehicle.licensePlate}</span>
                      ))}
                    </li>
                  </div>
                ))}
              </Collapse.Panel>
            ))}
          </Collapse>
        </div>
      );
    } else {
      content = (
        <div className="card-list-container">
          {dataList.map((row, rowIndex) => (
            <li className='card-list-li' key={rowIndex}>{row}</li>
          ))}
        </div>
      );
    }

    Modal.info({
      title,
      content,
      onOk: () => { },
      className: 'custom-modal',
      width: 500
    });
  };

  const onlineVehicleInfo = () => {
    const vehicleList = props.vehicleListData || [];
    const vehicleNames = vehicleList
      .filter(vehicle => vehicle.isAlive)
      .map(vehicle => ({
        licensePlate: vehicle.licensePlate,
        projectName: vehicle.projectName
      }));
    listModal(vehicleNames, '在线车辆车牌号', true);
  };

  const unlineVehicleInfo = () => {
    const vehicleList = props.vehicleListData || [];
    const vehicleNames = vehicleList
      .filter(vehicle => !vehicle.isAlive)
      .map(vehicle => ({
        licensePlate: vehicle.licensePlate,
        projectName: vehicle.projectName
      }));
    listModal(vehicleNames, '离线车辆车牌号', true);
  };

  const vehicleInfo = () => {
    const newVehicleList = project.vehicleList || '';
    const vehicleIds = newVehicleList.split(',');
    const vehicleList = props.vehicleListData || [];
    const vehicleNames = vehicleList
      .filter(vehicle => vehicleIds.includes(vehicle.licensePlate))
      .map(vehicle => ({
        licensePlate: vehicle.licensePlate,
        projectName: vehicle.projectName
      }));
    listModal(vehicleNames, '新增设备车牌号', true);
  };

  const projectInfo = () => {
    const projectList = project.projectList || '';
    const projects = projectList.split(',');
    listModal(projects, '新增项目');
  };

  const sDisplayFlexCenter = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  const sAvatarICON = {
    padding: '6px',
    width: '40px',
    height: '40px',
    borderRadius: '4px'
  };

  const sSvgIconSize = {
    width: 24,
    height: 24
  };

  const CardComponent = ({ title, count, unit, onClick, BgComponent, SvgComponent, color, border }) => (
    <Card style={{ minWidth: '11em', border: border || '1px solid transparent' }} onDoubleClick={onClick}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <div style={sDisplayFlexCenter}>
          <div>
            <p style={{ textTransform: 'uppercase', fontSize: '15px' }}>{title}</p>
            <h4 style={{ fontSize: '18px', marginBottom: 0 }}>
              <CountUp start={0} end={count} duration={1} decimals={title === '今日上线率' ? 2 : 0} />
              <span style={{ fontSize: '18px', marginLeft: '0.375em' }}>{unit}</span>
            </h4>
          </div>
        </div>
        <div style={sDisplayFlexCenter}>
          <BgComponent style={{ ...sAvatarICON, ...sDisplayFlexCenter }}>
            <SvgComponent style={{ color, ...sSvgIconSize }} fill="currentColor" />
          </BgComponent>
        </div>
      </div>
    </Card>
  );

  console.log('onlineVehicleCount', onlineTodayCount);
  return (
    <React.Fragment>
      <Flex wrap={false} justify="center" align="center" gap="middle" style={{ margin: customStyles.margin, marginBottom: 0 }}>
        {showNewCard && (
          <CardComponent
            title="项目数量"
            count={projectCount}
            unit="个"
            onClick={popProject}
            BgComponent={BgPrimary}
            SvgComponent={SvgOilWell}
            color={customStyles.colorPrimary}
          />
        )}

        <CardComponent
          title="项目工区"
          count={siteCount}
          unit="个"
          onClick={popSite}
          BgComponent={BgSecondary}
          SvgComponent={SvgIndustry}
          color={customStyles.colorSecondary}
        />

        <CardComponent
          title="总用户数"
          count={userCount}
          unit="个"
          onClick={popUsers}
          BgComponent={BgPrimary}
          SvgComponent={SvgUsers}
          color={customStyles.colorPrimary}
        />

        <CardComponent
          title="总车辆数"
          count={vehicleCount}
          unit="个"
          onClick={popVehicles}
          BgComponent={BgSecondary}
          SvgComponent={SvgTruck}
          color={customStyles.colorSecondary}
        />
        <CardComponent
          title="在线车辆"
          count={onlineCount}
          unit="辆"
          onClick={onlineVehicleInfo}
          BgComponent={BgSuccess}
          SvgComponent={SvgTruck}
          color={customStyles.colorSuccess}
        />

        <CardComponent
          title="离线车辆"
          count={offlineCount}
          unit="辆"
          onClick={unlineVehicleInfo}
          BgComponent={BgInfo}
          SvgComponent={SvgTruck}
          color={customStyles.colorTextDisabled}
          border={alarmStyle ? `1px solid ${customStyles.colorDanger}` : `1px solid ${customStyles.colorTextDisabled}`}
        />

        <CardComponent
          title="今日上线率"
          count={onlineTodayCount*100}
          unit="(%)"
          BgComponent={BgProject}
          SvgComponent={SvgEmergency}
          color={customStyles.colorDanger}
        />
        <CardComponent
          title="月新增设备"
          count={newVehicle}
          unit="台"
          onClick={vehicleInfo}
          BgComponent={BgDevice}
          SvgComponent={SvgDevice}
          color={customStyles.colorDanger}
        />
        {showNewCard && (
          <CardComponent
            title="月新增项目"
            count={newProject}
            unit="个"
            onClick={projectInfo}
            BgComponent={BgProject}
            SvgComponent={SvgProject}
            color={customStyles.colorDanger}
          />
        )}
      </Flex>
    </React.Fragment>
  );
};

export default Widget;
